import { ISearchStore } from "@search";
import { autobind } from "@bonliva-crm/common";
import { ObservableQuery } from "@apollo/client";
import { Subscription } from "zen-observable-ts";
import { action, computed, observable, runInAction } from "mobx";

import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { BookingWeekQueryInput, BookingSearch_CloneBooking } from "@bonliva-crm/graphql";
import { GroupedVirtualSearchManager } from "@search/GroupedVirtualSearchManager";
import { parseItems, Row, Item, BaseRowData } from "./utils";
import { BookingFilterManager } from "./BookingFilterManager";
import { useConfirmDialog } from "@hooks";
import { OkCancelDialogOptions } from "@containers";

import { logSubscriptionError } from "@utils/log-subscription-error";
export abstract class BookingSearchStore<RowType extends BaseRowData>
  implements ISearchStore
{
  searchManager: GroupedVirtualSearchManager<Row<Item<RowType>>>;
  filterManager: BookingFilterManager;
  @observable isDirectBookingEditorOpen = false;
  @observable total: number | null = null;
  @observable private _isLoading = false;

  queryFuncWatch: ObservableQuery;
  subscription: Subscription;

  protected constructor(
    private query: any,
    private patch?: (i: RowType) => RowType,
    private getTotal?: (filter: BookingWeekQueryInput) => Promise<number>
  ) {
    autobind(this);

    this.filterManager = BookingFilterManager.Create({});
    this.searchManager = new GroupedVirtualSearchManager(
      (skip, limit) => this.fetch(typeof skip === "number" ? { skip, limit } : undefined),
      true
    );

    this.filterManager.unhideFields();
    this.filterManager.fetchEmitter.on(this.fetch);

    this.init();
  }

  init = () => {
    const filter = this.filterManager.queryInput;
    this.subscription?.unsubscribe();

    this.queryFuncWatch = this.query.funcWatch(
      { filter, limit: 50, skip: 0 },
      { fetchPolicy: "network-only" }
    );

    this.subscription = this.queryFuncWatch.subscribe({
      next: ({ data }) => {
        this.searchManager.merge(
          parseItems(
            this.searchManager.items.last(),
            data.bookingWeeks.items.map(this.patch ? this.patch : (x) => x)
          )
        );
        this.searchManager.setGroup(0);
        this.searchManager.iLoaderForceUpdate();
      },
      error: logSubscriptionError("BookingSearchStore")
    });

    if (this.getTotal) {
      this.total = null;

      this.getTotal(filter).then((total) => {
        runInAction(() => {
          this.total = total;
        });
      });
    }
  };

  unsubscribe = () => {
    this.subscription.unsubscribe();
    this.filterManager.fetchEmitter.off(this.fetch);
  };

  fetch(opts?: { skip: number; limit: number }) {
    const skip = opts && opts.skip ? opts.skip : 0;
    const limit = opts && opts.limit ? opts.limit : 50;

    const filter = this.filterManager.queryInput;
    this.isLoading = true;
    const isNew = skip === 0;
    const isRefresh = isNew && this.searchManager.items.length > 0;

    if (isNew) {
      this.searchManager.clear(isRefresh);
      this.searchManager.setGroup(0);
    }

    if (isNew) {
      this.init();
      return Promise.resolve();
    } else {
      return this.queryFuncWatch.fetchMore({
        variables: { skip, limit, filter },
        updateQuery: (previousQueryResult, options) => {
          if (!previousQueryResult?.bookingWeeks) {
            return options.fetchMoreResult;
          }

          return {
            bookingWeeks: {
              ...previousQueryResult.bookingWeeks,
              ...options.fetchMoreResult.bookingWeeks,
              items: [
                ...previousQueryResult.bookingWeeks.items,
                ...options.fetchMoreResult.bookingWeeks.items
              ]
            },
            bookingWeeksTotal: null
          };
        }
      });
    }
  }

  @computed set isLoading(v: boolean) {
    this._isLoading = v;
  }

  get isLoading() {
    return this._isLoading;
  }

  @action openBookingEditor = () => {
    this.isDirectBookingEditorOpen = true;
  };

  @action closeBookingEditor = () => {
    this.isDirectBookingEditorOpen = false;
  };

  @action cloneBookingForMonthlyBrake = async (bookingId: string) => {
    const { confirm } = useConfirmDialog();
    const confirmResult = await confirm(
      new OkCancelDialogOptions(locale.bookingCloningQuestion)
    );

    if (confirmResult === "cancel") {
      return;
    }

    BookingSearch_CloneBooking.func({ bookingId })
      .then((result) => {
        this.fetch();
        store().snackbars.success(locale.bookingClonedSuccess);
        window.open(`/bookings/${result.data.cloneBooking}`, "_blank");
      })
      .catch((e) => {
        store().snackbars.error(locale.bookingClonedFailure);
      });
  };
}
